import {
  authDescriptionSlate,
  authHeadingSlate,
  authOutlinedInputSurfaceSx,
  authRememberLabel,
  borderInput,
  palettePrimaryMain,
  surfaceAuthCard,
  textPrimary,
} from '@/theme/designTokens'

/** Figma 5074-11759 — Email / Password (#FFFFFF); overrides MUI InputBase `background: none` on `<input>`. */
export const loginInputSx = {
  ...authOutlinedInputSurfaceSx,
  height: 50,
  borderRadius: '8px',
  fontSize: '16px',
  letterSpacing: '-0.9525px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
    px: '16px',
    py: '14px',
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: `0 0 0 100px ${surfaceAuthCard} inset`,
    WebkitTextFillColor: textPrimary,
    caretColor: textPrimary,
  },
} as const

/** Inline on native `<input>` — wins over MUI `background: none` when sx specificity is insufficient. */
export const loginInputNativeProps = {
  style: {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
} as const

/** Figma 5074-11759 — Welcome heading (scaled down on phones so it does not overflow). */
export const loginWelcomeHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: { xs: '36px', sm: '48px', md: '56px' },
  lineHeight: { xs: '40px', sm: '48px', md: '56px' },
  letterSpacing: { xs: '-1.5px', sm: '-2.4px', md: '-3.0592px' },
  color: authHeadingSlate,
  textAlign: 'center',
} as const

export const loginWelcomeSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '-0.4304px',
  color: authDescriptionSlate,
  textAlign: 'center',
} as const

/** Figma primary CTA — Login / Continue Login */
export const loginPrimaryButtonSx = {
  bgcolor: palettePrimaryMain,
  borderRadius: '12px',
  minHeight: 56,
  px: '24px',
  py: '16px',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '24px',
  textTransform: 'none',
  color: '#ffffff',
  '&:hover': {
    bgcolor: '#007bb8',
  },
  '&.Mui-disabled': {
    bgcolor: '#94a3b8',
    color: '#ffffff',
  },
} as const

export const loginRememberRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mt: '14px',
  width: '100%',
} as const

export const loginRememberLabelSx = {
  '& .MuiFormControlLabel-label': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '-0.6px',
    color: authRememberLabel,
  },
} as const

export const loginForgotLinkSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '-0.6px',
  color: palettePrimaryMain,
  textTransform: 'none',
  minWidth: 0,
  p: 0,
} as const

export const loginFieldErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#ef4444',
  mt: '10px',
} as const

/** Sensemaker login — server error below password row, above submit (not top Alert). */
export const loginServerErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#ef4444',
} as const

/** Figma 5074-12371 right panel — shared by /login and /set-password (5074-12353). */
export const authSplitFormPageSx = {
  width: '100%',
  maxWidth: 400,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: { xs: '24px', md: 'clamp(28px, 5vh, 56px)' },
  boxSizing: 'border-box',
  flexShrink: 0,
  /** md+: center in the right panel. xs: parent already centers in the remaining viewport. */
  my: { xs: 0, md: 'auto' },
} as const

/** Form fields region — must not clip; parent login-form-panel scrolls on short viewports. */
export const authSplitFormBodySx = {
  width: '100%',
  flexShrink: 0,
} as const

/** Figma 5074-12353 — logo + title stack in right panel. */
export const authSplitFormHeaderSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
} as const

export const authSplitFormLogoSx = {
  width: { xs: 180, sm: 237 },
  height: { xs: 82, sm: 108 },
  objectFit: 'contain',
  display: 'block',
  mb: { xs: '8px', sm: '16px' },
} as const

/** Figma 5074-12364 / 5074-12367 — email label + 50px input. */
export const authSplitEmailLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.9525px',
  color: authHeadingSlate,
  m: 0,
  mb: '8px',
  display: 'block',
} as const
